import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { MessageService } from 'primeng/api';
import { InlineErrorComponent } from '@org/shared';
import { CvUploadService } from '../cv-upload.service';

export interface CvUploadSuccess {
  correlationId: string;
  fileName: string;
}

@Component({
  selector: 'lib-cv-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    ProgressBarModule,
    InlineErrorComponent,
  ],
  templateUrl: './cv-upload.html',
  styleUrl: './cv-upload.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CvUploadComponent {
  private readonly svc            = inject(CvUploadService);
  private readonly messageService = inject(MessageService);

  // ── Inputs / Outputs ───────────────────────────────────────────────────────
  readonly jobId              = input.required<string>();
  readonly jobTitle           = input<string>('');
  /** Optional pre-captured details from a parent form (e.g. the applicants
   *  intake drawer). When set, these are forwarded on submit alongside the
   *  file so the backend can apply them to the created candidate. */
  readonly phoneNumber        = input<string>('');
  readonly currentSalary      = input<number | null>(null);
  readonly desiredSalaryMin   = input<number | null>(null);
  readonly desiredSalaryMax   = input<number | null>(null);
  readonly currency           = input<string>('');
  /** Pre-filled name / email that override the component's own inputs when
   *  supplied — the parent has already collected them so no re-typing. */
  readonly presetName         = input<string>('');
  readonly presetEmail        = input<string>('');
  readonly uploaded           = output<CvUploadSuccess>();

  // ── State ──────────────────────────────────────────────────────────────────
  readonly isDragging   = signal(false);
  readonly isUploading  = signal(false);
  readonly selectedFile = signal<File | null>(null);
  /**
   * Inline error chip rendered inside the upload panel when submit()
   * rejects. Cleared when the user picks a new file, removes the file,
   * or clicks Retry. Replaces the previous transient error toast — the
   * selected file stays in the drop zone, so Retry is one click rather
   * than a fresh pick-the-file-again dance.
   */
  readonly uploadError  = signal<string | null>(null);
  candidateName  = '';
  candidateEmail = '';

  // ── Drag & drop ────────────────────────────────────────────────────────────

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const file = event.dataTransfer?.files?.[0];
    if (file) this.setFile(file);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0];
    if (file) this.setFile(file);
    // Reset so re-selecting same file triggers change event
    input.value = '';
  }

  removeFile(): void {
    this.selectedFile.set(null);
    this.uploadError.set(null);
  }

  // ── Upload ─────────────────────────────────────────────────────────────────

  submit(): void {
    const file = this.selectedFile();
    if (!file) return;

    this.isUploading.set(true);
    this.uploadError.set(null);

    this.svc.uploadCv(
      this.jobId(),
      file,
      {
        // Parent-provided values win over the component's own inputs when set,
        // so a drawer that already collected name / email doesn't need the
        // recruiter to re-type them here.
        candidateName:    this.presetName()  || this.candidateName  || undefined,
        candidateEmail:   this.presetEmail() || this.candidateEmail || undefined,
        phoneNumber:      this.phoneNumber() || undefined,
        currentSalary:    this.currentSalary(),
        desiredSalaryMin: this.desiredSalaryMin(),
        desiredSalaryMax: this.desiredSalaryMax(),
        currency:         this.currency()    || undefined,
      },
    ).subscribe({
      next: ({ correlationId }) => {
        this.isUploading.set(false);
        this.selectedFile.set(null);
        this.candidateName  = '';
        this.candidateEmail = '';
        this.messageService.add({
          severity: 'success',
          summary:  'CV Submitted',
          detail:   `${file.name} has been uploaded and queued for processing.`,
        });
        this.uploaded.emit({ correlationId, fileName: file.name });
      },
      error: () => {
        this.isUploading.set(false);
        this.uploadError.set(
          "We couldn't upload this CV. Try again — the file's still selected.",
        );
      },
    });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private setFile(file: File): void {
    // A fresh pick supersedes any prior error — the user is starting over.
    this.uploadError.set(null);
    const allowed = ['.pdf', '.doc', '.docx'];
    const ext     = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowed.includes(ext)) {
      this.messageService.add({
        severity: 'warn',
        summary:  'Unsupported File',
        detail:   'Please upload a PDF, DOC, or DOCX file.',
      });
      return;
    }
    this.selectedFile.set(file);
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024)        return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
